import {
	initializeOrganizationId,
	provideSettings,
} from "@/composables/settings";
import {
	configureAWS,
	getSharedMqttJsClient,
	initClientReactive,
	initCloudMqtt,
	isUserGroupsAuthorized,
	setCustomerId,
	setOrganizationId,
	useCustomerId,
} from "@ptx-autonomous/libs/api";
import {
	setConfirmComponent,
	ConfirmDialogContent,
} from "@ptx-autonomous/libs/confirm";
import { provideMapGlobal } from "@ptx-autonomous/libs/maplibre";
import { initOpenGlClient } from "@ptx-autonomous/libs/opengl-api";
import { loadIcons } from "@ptx-autonomous/libs/ui";
import * as Sentry from "@sentry/vue";
import {
	checkActiveRegion,
	getApiUrl,
	getAwsConfiguration,
} from "@shared/configuration/aws-configuration";
import { getAwsExports } from "@shared/configuration/aws-exports";
import { userGroupPermissionsSettings } from "@shared/permissions-settings";
import { setUser } from "@shared/sentry";
import { cloudWatchLogger } from "@shared/utils/cloudwatch-logger";
import { initializeConsoleOverride } from "@shared/utils/console-override";
import { VueQueryPlugin } from "@tanstack/vue-query";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { CustomWiggle } from "gsap/CustomWiggle";
import { computed, createApp } from "vue";
import App from "./App.vue";
import { i18n } from "./i18n";
import router from "./router";
import "./theme.css";

const app = createApp(App);

if (import.meta.env.PROD) {
	Sentry.init({
		Vue: app,
		dsn: "https://e8c0a836e101a2e5114a5f5763f977bd@o4506507993481216.ingest.sentry.io/4506792051933184",
		integrations: [
			Sentry.browserTracingIntegration({
				router,
			}),
			Sentry.replayIntegration({
				maskAllText: false,
				blockAllMedia: false,
			}),
			Sentry.replayCanvasIntegration(),
			Sentry.httpClientIntegration({
				failedRequestStatusCodes: [[500, 599]], // Capture failed requests with status codes 500-599
				failedRequestTargets: [/^(?:(?!arcgis).)*$/], // Ignore failed requests to map server
			}),
			Sentry.feedbackIntegration({
				// Additional SDK configuration goes in here, for example:
				colorScheme: "system",
				autoInject: false,
			}),
		],
		// Performance Monitoring
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: [],
		// Session Replay
		replaysSessionSampleRate: 0.5, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		ignoreErrors: [
			"AMQJS0011E Invalid state", // Ignore no MQTT connection error
			`"PushNotifications" plugin is not implemented on web`,
			/^AMQJS0011E Invalid state not connected.$/,
			`No id token found`,
			/^Unable to complete output ring starting at.+$/,
			"Notifications permission denied: denied",
			/^Unable to find segment.+$/,
			"InternalError: too much recursion",
			/^Failed to fetch dynamically imported module:.+$/,
			"undefined is not an object (evaluating 'e.coords')",
		],
	});
}

setUser();
const { environment, apiVersion, organizationId } = provideSettings(app);

// Resolve which region (primary/failover) is active before reading any AWS
// config below, so the very first boot already talks to the right region.
await checkActiveRegion(environment.value);
const awsConfig = getAwsConfiguration(environment.value);

configureAWS(getAwsExports(environment.value));

if (import.meta.env.PROD) {
	initializeConsoleOverride();
	await cloudWatchLogger.initialize(
		apiVersion.value,
		__APP_VERSION__,
		awsConfig.region,
		"/web/portal",
	);
}

initCloudMqtt(awsConfig.region, awsConfig.host);

// Initialize the shared mqtt.js client for AWS IoT Core
const sharedMqttJs = getSharedMqttJsClient({
	type: "aws",
	host: awsConfig.host,
	region: awsConfig.region,
	protocolVersion: 5,
	clientIdPrefix: "portal-mqtt",
});
sharedMqttJs.connect();

setConfirmComponent(app, ConfirmDialogContent);

await initializeOrganizationId();
//Set the organization id if the user is an admin
if (
	await isUserGroupsAuthorized(
		userGroupPermissionsSettings.allowedGroupsToSwitchOrganization ?? [
			"FlightPathAdmin",
		],
	)
) {
	await setOrganizationId(organizationId.value || useCustomerId());
} else {
	await setCustomerId(false);
}

initClientReactive(
	computed(() => {
		return getApiUrl(environment.value, apiVersion.value);
	}),
	true,
);
initOpenGlClient(getApiUrl(environment.value, apiVersion.value));
provideMapGlobal(app);

const icons = import.meta.glob("@shared/assets/icons/**/*.svg", {
	query: "raw",
	import: "default",
	eager: true,
}) as Record<string, string>;
await loadIcons(icons, app);

gsap.registerPlugin(CustomEase, CustomWiggle);

app.use(router);
app.use(VueQueryPlugin);

app.use(i18n);
app.mount("#app");
